<template>
  <PageHeader :title="$t('ui.entities.reloop.label')">
    <template #right>
      <div class="flex gap-2 items-center flex-col md:flex-row md:gap-4">
        <div
          v-if="dealershipsList.length > 1"
          class="z-10"
        >
          <Select
            v-model="dealershipsSelected"
            :options="dealershipsList"
            mode="multiple"
            :chips="false"
            searchable
            placeholder="Select a Dealership"
            option-group-label="manufacturer"
            option-group-children="dealerships"
            class="min-w-80"
            @update:model-value="onDealershipChange"
          />
        </div>
        <div
          v-else
          class="flex"
        >
          <Icon
            type="location-dot"
            class="mr-1"
          />
          <div>{{ dealershipsList[0]?.dealerships[0]?.label }}</div>
        </div>
        <div class="w-full">
          <DatePicker
            v-model="dateRange"
            selection-mode="range"
            show-presets
            class="min-w-60"
            :presets="dateRangePresets"
            :months-visible="useBreakPoints({ xs: 1, sm: 2 })"
            @select="onDateRangeChange"
          />
        </div>
      </div>
    </template>
  </PageHeader>
  <div class="grid gap-4 grid-cols-1 md:grid-cols-3 mb-4">
    <div>
      <Card
        :is-loading="reloopsApiRequest.isLoading.value"
        no-margin
      >
        <CardBody>
          <div class="flex items-center justify-between">
            <Avatar
              size="lg"
              icon="warning"
              no-border
              rounding="rounded"
              :color="dashboardData.flaggedForReview ? 'warning' : 'secondary'"
            />
            <div class="ml-4 w-full">
              <div class="text-xl font-medium leading-5">{{ dashboardData.flaggedForReview }}</div>
              <div class="text-sm text-secondary">Flagged for review</div>
            </div>

            <Button
              icon="chevron-right"
              plain
              severity="secondary"
              :router-link="`/reports/reloop/submissions?dateRange=${dateRange}&dealerships=${dealershipsSelected}&alertManagement=true`"
            />
          </div>
        </CardBody>
      </Card>
    </div>
    <Card
      :is-loading="reloopsApiRequest.isLoading.value"
      no-margin
      class="md:col-span-2"
    >
      <CardBody class="grid gap-4 grid-cols-1 sm:grid-cols-3">
        <Statistic
          v-model="dashboardData.submissions.current"
          v-model:last-period-value="dashboardData.submissions.previous"
          label="Submissions"
          icon="inbox-in"
          :layout="useBreakPoints({ xs: 'horizontal', sm: 'vertical', lg: 'horizontal' })"
          :icon-size="useBreakPoints({ xs: 'md', lg: 'lg' })"
          :difference-tooltip="`From previous period (${dashboardData.submissions.previous})`"
        />
        <Statistic
          v-model="dashboardData.perfectScore.current"
          v-model:last-period-value="dashboardData.perfectScore.previous"
          label="Perfect score"
          icon="hundred-points"
          :layout="useBreakPoints({ xs: 'horizontal', sm: 'vertical', lg: 'horizontal' })"
          :icon-size="useBreakPoints({ xs: 'md', lg: 'lg' })"
          color="success"
          :difference-tooltip="`From previous period (${dashboardData.perfectScore.previous})`"
        />
        <Statistic
          v-model="dashboardData.averageSatisfaction.current"
          v-model:last-period-value="dashboardData.averageSatisfaction.previous"
          label="Average Satisfaction"
          icon="thumbs-up"
          :layout="useBreakPoints({ xs: 'horizontal', sm: 'vertical', lg: 'horizontal' })"
          :icon-size="useBreakPoints({ xs: 'md', lg: 'lg' })"
          color="info"
          :difference-tooltip="`From previous period (${dashboardData.averageSatisfaction.previous})`"
        >
          <template #statAfter><span class="text-secondary ml-2 whitespace-nowrap">/ 10</span></template>
        </Statistic>
      </CardBody>
    </Card>
  </div>

  <div class="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
    <div>
      <Card
        :is-loading="reloopsApiRequest.isLoading.value"
        no-margin
      >
        <CardBody>
          <CardTitle title="Results" />
          <div class="flex justify-between items-center">
            <div class="flex items-center">
              <div class="w-[80px] -mx-4 -my-2 mr-2">
                <RadialMiniChart v-model="dashboardData.attended.percentage" />
              </div>
              <div>
                <div class="font-medium">Attended</div>
                <div class="text-secondary">{{ dashboardData.attended.count }} customers</div>
              </div>
            </div>
            <div>
              <Button
                icon="chevron-right"
                plain
                severity="secondary"
                :router-link="`/reports/reloop/submissions?dateRange=${dateRange}&dealerships=${dealershipsSelected}&attended=true`"
              />
            </div>
          </div>
          <div class="flex justify-between items-center mt-4">
            <div class="flex items-center">
              <div class="w-[80px] -mx-4 -my-2 mr-2">
                <RadialMiniChart
                  v-model="dashboardData.dealt.percentage"
                  v-tippy="'of those who attended'"
                />
              </div>
              <div>
                <div class="font-medium">Dealt</div>
                <div class="text-secondary">{{ dashboardData.dealt.count }} customers</div>
              </div>
            </div>
            <div>
              <!-- 
                <Button icon="chevron-right" plain severity="secondary" :router-link="`/reports/reloop/submissions?dateRange=${dateRange}&dealerships=${dealershipsSelected}&dealt=true`" />
      -->
            </div>
          </div>
          <div class="flex justify-between items-center mt-4">
            <div class="flex items-center">
              <div class="w-[80px] -mx-4 -my-2 mr-2">
                <RadialMiniChart
                  v-model="dashboardData.reappointed.percentage"
                  v-tippy="'of those who didn\'t deal'"
                />
              </div>
              <div>
                <div class="font-medium">Reappointed</div>
                <div class="text-secondary">{{ dashboardData.reappointed.count }} customers</div>
              </div>
            </div>
            <div>
              <!-- 
                <Button icon="chevron-right" plain severity="secondary" :router-link="`/reports/reloop/submissions?dateRange=${dateRange}&dealerships=${dealershipsSelected}&reappointed=true`" />
              -->
            </div>
          </div>
        </CardBody>
        <Separator />
        <CardBody class="flex flex-col md:flex-row">
          <div class="w-full">
            <div class="text-lg font-medium w-full mb-1">Score</div>
            <div class="text-sm text-secondary">Based on period submissions</div>
          </div>
          <div class="mt-4 md:mt-0 max-w-[200px]">
            <apexchart
              type="radialBar"
              :options="apexChartDefaults.halfGauge.options"
              :series="isNaN(dashboardData.score) ? ['-'] : [dashboardData.score.toFixed(0)]"
              class="-mt-2"
            ></apexchart>
          </div>
        </CardBody>
      </Card>
    </div>
    <div class="flex flex-col gap-4">
      <Card
        :is-loading="reloopsApiRequest.isLoading.value"
        no-margin
      >
        <CardBody>
          <CardTitle
            title="Customer Satisfaction"
            :has-margin="false"
          />
          <apexchart
            type="bar"
            height="180"
            :options="customerSatisfaction.options"
            :series="dashboardData.satisfactionHistory.series"
          ></apexchart>
        </CardBody>
      </Card>
      <Card
        :is-loading="reloopsApiRequest.isLoading.value"
        no-margin
      >
        <!-- <CardBody class="flex items-center">
          <Avatar icon="trophy" size="lg" color="primary" />
          <div class="ml-4">
            <div class="text-secondary text-sm">Customer Satisfaction Ranking</div>
            <div class="text-2xl font-medium">Top 5%</div>
          </div>
        </CardBody> -->
        <CardBody class="flex items-center">
          <Avatar
            :icon="dashboardData.satisfactionTrend > 0 ? 'chart-line-up' : 'chart-line-down'"
            size="lg"
            no-border
            :color="dashboardData.satisfactionTrend > 0 ? 'success' : 'danger'"
          />
          <div class="ml-4">
            <div class="text-secondary text-sm">Customer Satisfaction Trend</div>
            <div
              v-tippy="'from previous period'"
              class="flex gap-2 items-center"
              :class="[dashboardData.satisfactionTrend > 0 ? 'text-success' : 'text-error']"
            >
              <Icon :type="dashboardData.satisfactionTrend > 0 ? 'arrow-up' : 'arrow-down'" />
              <div class="text-2xl font-medium">
                {{
                  dashboardData.satisfactionTrend != null ? toPercent(dashboardData.satisfactionTrend / 100, 2) : '-'
                }}
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
    <Card
      :is-loading="reloopsApiRequest.isLoading.value"
      no-margin
      class="sm:col-span-2"
    >
      <CardBody>
        <CardTitle title="Latest Feedback" />
        <ListGroup v-if="dashboardData.latestFeedback.length > 0">
          <ListGroupItem
            v-for="feedback in dashboardData.latestFeedback"
            :key="feedback"
            class="flex items-center text-sm hover:text-primary"
            :route="`/agreement/${feedback.agreementId}/appointments/${feedback.appointmentId}`"
          >
            <Avatar
              v-if="feedback.satisfaction != null"
              v-tippy="{ content: 'Satisfaction: ' + feedback.satisfaction }"
              :icon="feedback.satisfaction === 10 ? 'star' : `${feedback.satisfaction}`"
              size="sm"
            />

            <Avatar
              v-else
              v-tippy="{ content: 'Not Rated' }"
              icon="comment"
              size="sm"
            />
            <div class="ml-4">"{{ feedback.text }}"</div>
          </ListGroupItem>
        </ListGroup>
        <div
          v-else
          class="text-secondary text-sm"
        >
          No feedback in the last 6 months
        </div>
        <br />
        <CardTitle title="Latest Bad Experiences" />
        <ListGroup v-if="dashboardData.latestComplaints.length > 0">
          <ListGroupItem
            v-for="complaint in dashboardData.latestComplaints"
            :key="complaint"
            class="flex items-center text-sm hover:text-primary"
            :route="`/agreement/${complaint.agreementId}/appointments/${complaint.appointmentId}`"
          >
            <Avatar
              v-if="complaint.satisfaction != null"
              v-tippy="{ content: 'Satisfaction' }"
              :icon="complaint.satisfaction === 10 ? 'star' : `${complaint.satisfaction}`"
              size="sm"
            />

            <Avatar
              v-else
              v-tippy="{ content: 'Not Rated' }"
              icon="comment"
              size="sm"
            />
            <div class="ml-4">"{{ complaint.text }}"</div>
          </ListGroupItem>
        </ListGroup>
        <div
          v-else
          class="text-secondary text-sm"
        >
          No complaints in the last 6 months
        </div>
      </CardBody>
    </Card>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'

import PageHeader from '@/components/page/PageHeader.vue'
import Card from '@/components/card/Card.vue'
import CardBody from '@/components/card/CardBody.vue'
import CardTitle from '@/components/card/CardTitle.vue'
import Button from '@/components/button/Button.vue'
import ListGroup from '@/components/list/ListGroup.vue'
import ListGroupItem from '@/components/list/ListGroupItem.vue'
import Avatar from '@/components/avatar/Avatar.vue'
import Icon from '@/components/icon/Icon.vue'
import RadialMiniChart from '@/components/dashboard/charts/RadialMiniChart.vue'
import Statistic from '@/components/dashboard/stats/Statistic.vue'
import DatePicker from '@/components/forms/DatePicker.vue'
import Select from '@/components/forms/Select.vue'

import apexChartDefaults from '@/components/charts/apexChartDefaults'

// composables
import useApiRequest from '@/composables/useApiRequest'
import { useBreakPoints } from '@/composables/useWindowSize'

// stores
import { useUserStore } from '@/stores/UserStore.ts'

// utilities
import { formatNumber } from '@/utilities/number'
import groupDealershipsByManufacturer from '@/utilities/groupDealerships'
import { toPercent } from '@/utilities'

import dayjs from 'dayjs'
import Separator from '@/components/separator/Separator.vue'
import { alertToast } from '@/utilities/notification'

const userStore = useUserStore()
const dealershipsSelected = ref([
  JSON.parse(localStorage.getItem('accessSelected')).dealerships[0] ?? userStore.accessAllowed.dealerships[0].id
])
const dealershipsList = ref([])

onMounted(async () => {
  // If user is Call Agent, only allow single selection
  dealershipsList.value = await groupDealershipsByManufacturer(userStore.accessAllowed.dealerships)
})

const dateRange = ref([dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().startOf('day').format('YYYY-MM-DD')])
const dateRangePresets = [
  {
    label: 'This Week',
    value: {
      start: dayjs().startOf('week'),
      end: dayjs().endOf('week')
    }
  },
  {
    label: 'This Month',
    value: {
      start: dayjs().startOf('month'),
      end: dayjs().endOf('month')
    }
  },
  {
    label: 'Last Month',
    value: {
      start: dayjs().subtract(1, 'month').startOf('month'),
      end: dayjs().subtract(1, 'month').endOf('month')
    }
  }
]

const reloopsFetched = ref([])
const reloopsSixMonthPeriod = ref([])

const reloopsApiRequest = useApiRequest()
// Get reloops
async function fetchReloops() {
  if (!dateRange.value[0] || !dateRange.value[1] || !dealershipsSelected.value.length) {
    return
  }
  try {
    await reloopsApiRequest
      .send({
        endpoint: '/v1/reloop',
        params: {
          startDate: dayjs(dateRange.value[0]).subtract(6, 'month').startOf('month').format('YYYY-MM-DD'),
          endDate: dayjs(dateRange.value[1]).format('YYYY-MM-DD'),
          dealerships: dealershipsSelected.value.join(','),
          returnQuestionsById: '11,12'
        }
      })
      .then(response => {
        if (response.success) {
          reloopsFetched.value = response.data
        } else {
          alertToast('Failed to fetch ReLoops', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to fetch ReLoops', err.message, 'error')
  }
}

watch(reloopsFetched, () => {
  reloopsSixMonthPeriod.value = JSON.parse(JSON.stringify(reloopsFetched.value))
})

fetchReloops().then(() => {
  reloopsSixMonthPeriod.value = JSON.parse(JSON.stringify(reloopsFetched.value))
})

const stateInit = ref({
  flaggedForReview: null,
  submissions: {
    current: null,
    previous: null
  },
  perfectScore: {
    current: null,
    previous: null
  },
  averageSatisfaction: {
    current: null,
    previous: null
  },
  rebooked: null,
  attended: null,
  dealt: null,
  reAppointed: null,
  score: null,
  satisfactionHistory: {
    months: [],
    series: {
      name: 'Satisfaction',
      data: []
    }
  },
  leaderboardRank: null,
  satisfactionTrend: null,
  feedback: [],
  complaints: []
})

const dashboardData = computed(() => {
  const reloopsCurrentPeriod = reloopsFetched.value.filter(reloop =>
    dayjs(reloop.createdAt).isBetween(dayjs(dateRange.value[0]), dayjs(dateRange.value[1]))
  )
  const reloopsPreviousPeriod = reloopsFetched.value.filter(reloop =>
    dayjs(reloop.createdAt).isBetween(
      dayjs(dateRange.value[0]).subtract(1, 'month'),
      dayjs(dateRange.value[1]).subtract(1, 'month')
    )
  )

  // For Review
  const flaggedForReview = reloopsCurrentPeriod.filter(reloop => reloop.alertManagement).length

  // Statistic Cards
  const perfectScore = reloopsCurrentPeriod.filter(reloop => reloop.scores.totals.totalPercent === 100).length
  const perfectScorePrev = reloopsPreviousPeriod.filter(reloop => reloop.scores.totals.totalPercent === 100).length

  const averageSatisfaction =
    reloopsCurrentPeriod.reduce((acc, reloop) => acc + reloop.satisfaction, 0) / reloopsCurrentPeriod.length
  const averageSatisfactionPrev =
    reloopsPreviousPeriod.reduce((acc, reloop) => acc + reloop.satisfaction, 0) / reloopsPreviousPeriod.length
  // Result Radial Mini Charts

  const attendedCount = reloopsCurrentPeriod.filter(reloop => reloop.attended).length
  const attendedPercentage = reloopsCurrentPeriod.length > 0 ? (attendedCount / reloopsCurrentPeriod.length) * 100 : 0

  const dealtCount = reloopsCurrentPeriod.filter(reloop => reloop.dealt).length
  const dealtPercentage = attendedCount > 0 ? (dealtCount / attendedCount) * 100 : 0

  const reappointedCount = reloopsCurrentPeriod.filter(reloop => reloop.reappointed).length
  const reappointedPercentage =
    reloopsCurrentPeriod.length - dealtCount > 0
      ? (reappointedCount / (reloopsCurrentPeriod.length - dealtCount)) * 100
      : 0

  // Get average score based on all submissions.scores.totals.totalPercent
  const score =
    reloopsCurrentPeriod.reduce((acc, reloop) => {
      if (reloop.scores.totals.totalPercent != null) {
        return acc + reloop.scores.totals.totalPercent
      } else {
        return acc
      }
    }, 0) / reloopsCurrentPeriod.length

  // Customer satisfaction history
  const customerSatisfactionMonths = []
  let rangeStart = dayjs().subtract(1, 'month').endOf('month').subtract(5, 'month').startOf('month').format()
  const rangeEnd = dayjs().subtract(1, 'month').endOf('month').format()

  const customerSatisfactionByMonth = []

  // Add each month to the array
  while (rangeStart <= rangeEnd) {
    customerSatisfactionMonths.push(dayjs(rangeStart).format('MMM'))

    // Get the average satisfaction for the looped month
    const satisfactionForMonth = reloopsSixMonthPeriod.value.filter(reloop =>
      dayjs(reloop.createdAt).isSame(rangeStart, 'month')
    )
    let averageSatisfactionForMonth =
      satisfactionForMonth.reduce((acc, reloop) => {
        if (reloop.satisfaction != null) {
          return acc + reloop.satisfaction
        } else {
          return acc
        }
      }, 0) / satisfactionForMonth.length

    // if NaN, set to 0
    if (isNaN(averageSatisfactionForMonth)) {
      averageSatisfactionForMonth = 0
    }

    // If is number, round to 2 decimal places
    if (typeof averageSatisfactionForMonth === 'number') {
      averageSatisfactionForMonth = formatNumber(averageSatisfactionForMonth, 'normal', 2)
    }

    customerSatisfactionByMonth.push(averageSatisfactionForMonth)

    rangeStart = dayjs(rangeStart).add(1, 'month').format()
  }

  // Calculate satisfaction trend based on data in customerSatisfactionByMonth. Show as percentage change to 2 dp
  let satisfactionTrend = null

  if (customerSatisfactionByMonth.length > 1) {
    // Calculate percentage change between previous period and current period satisfaction
    // Get last 2 items in the customerSatisfactionByMonth array
    const currentSatisfaction = customerSatisfactionByMonth[customerSatisfactionByMonth.length - 1]
    const previousSatisfaction = customerSatisfactionByMonth[customerSatisfactionByMonth.length - 2]

    satisfactionTrend =
      previousSatisfaction > 0 ? ((currentSatisfaction - previousSatisfaction) / previousSatisfaction) * 100 : 0

    // If NaN, return 0
    if (isNaN(satisfactionTrend)) {
      satisfactionTrend = 0
    }
  }

  const latestFeedback = reloopsFetched.value
    .filter(
      reloop =>
        reloop.questions && // Check if questions exist
        reloop.questions['11'] && // Check if question 11 exists
        reloop.questions['11'].answer !== null // Check if the answer is not null
    )
    .map(reloop => ({
      agreementId: reloop.agreementId,
      appointmentId: reloop.appointmentId,
      text: reloop.questions['11'].answer,
      satisfaction: reloop.satisfaction
    }))
    .slice(0, 5) // Get only the first 5 feedbacks

  // Get latest complaints
  const latestComplaints = reloopsFetched.value
    .filter(
      reloop =>
        reloop.questions && // Check if questions exist
        reloop.questions['12'] && // Check if question 12 exists
        reloop.questions['12'].answer !== null // Check if the answer is not null
    )
    .map(reloop => ({
      agreementId: reloop.agreementId,
      appointmentId: reloop.appointmentId,
      text: reloop.questions['12'].answer,
      satisfaction: reloop.satisfaction
    }))
    .slice(0, 5) // Get only the first 5 complaints

  return {
    ...stateInit,
    flaggedForReview,
    submissions: {
      current: reloopsCurrentPeriod.length,
      previous: reloopsPreviousPeriod.length
    },
    perfectScore: {
      current: perfectScore,
      previous: perfectScorePrev
    },
    averageSatisfaction: {
      current: formatNumber(averageSatisfaction, 'normal', 2),
      previous: formatNumber(averageSatisfactionPrev, 'normal', 2)
    },
    attended: {
      count: attendedCount,
      percentage: attendedPercentage
    },
    dealt: {
      count: dealtCount,
      percentage: dealtPercentage
    },
    reappointed: {
      count: reappointedCount,
      percentage: reappointedPercentage
    },
    score: score,
    satisfactionHistory: {
      months: customerSatisfactionMonths,
      series: [
        {
          name: 'avg',
          data: customerSatisfactionByMonth
        }
      ]
    },
    leaderboardRank: null,
    satisfactionTrend: satisfactionTrend,
    latestFeedback: latestFeedback,
    latestComplaints: latestComplaints
  }
})

// Customer Satisfaction
const customerSatisfaction = ref({
  options: {
    chart: {
      sparkline: {
        enabled: false
      },
      parentHeightOffset: 0,
      type: 'bar',
      toolbar: { show: false }
    },
    plotOptions: {
      bar: {
        barHeight: '100%',
        columnWidth: '15px',
        startingShape: 'rounded',
        endingShape: 'rounded',
        borderRadius: 6
      }
    },
    colors: ['var(--utility-warning-400)', '#5bc0de'],
    grid: {
      show: false,
      padding: {
        top: -30,
        left: -18,
        bottom: -7,
        right: -10
      }
    },
    dataLabels: { enabled: false },
    // tooltip: { enabled: false },
    legend: { show: false },
    xaxis: {
      categories: dashboardData.value.satisfactionHistory.months,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        style: {
          colors: `var(--text-secondary)`
        },
        show: true
      }
    },
    yaxis: { labels: { show: false } }
  }
})

function onDealershipChange() {
  // Refetch reloops

  fetchReloops()
}

function onDateRangeChange() {
  // Refetch reloops
  fetchReloops()
}
</script>

<style></style>
